import type {Editor} from 'grapesjs';
import type {ColorPaletteInfo} from '@Libraries/color-palettes.library';
import {ColorPaletteType} from '@Libraries/color-palettes.library';
import {getMainWrapper} from '@EmailMakerReact/lib/email-maker-component-query-library';
import {getComponentStyleProperty} from '@EmailMakerReact/lib/email-maker-component-style-library';
import {ICON_BUTTON_CONTROL_SET} from './controls/icon-button-control/icon-button-control';
import type {EmailMakerSectorProperty, IndexedSectorProperty} from './right-panel-registry.types';

const DEFAULT_COLUMNS_PRESET_ID = 1;

export function resolveColumnsPresetId(rawPresetId: unknown): number {
  if (typeof rawPresetId === 'number') {
    return rawPresetId;
  }
  if (typeof rawPresetId === 'string') {
    return parseInt(rawPresetId, 10) || DEFAULT_COLUMNS_PRESET_ID;
  }
  return DEFAULT_COLUMNS_PRESET_ID;
}

export function shouldUpdateColorPaletteOnApplication(palette: ColorPaletteInfo): boolean {
  return palette.type === ColorPaletteType.BrandKit;
}

/** CSS property key under which the applied color-palette JSON is stored on the email wrapper. */
const WRAPPER_PALETTE_STYLE_PROPERTY = 'color-palette';

export function getAppliedPaletteFromWrapper(editor: Editor): ColorPaletteInfo | null {
  const wrapper = getMainWrapper(editor);
  if (!wrapper) {
    return null;
  }
  const raw = getComponentStyleProperty(wrapper, WRAPPER_PALETTE_STYLE_PROPERTY);
  if (!raw) {
    return null;
  }
  try {
    return JSON.parse(raw) as ColorPaletteInfo;
  } catch {
    return null;
  }
}

export function isIconButtonControl(control: EmailMakerSectorProperty['control']): boolean {
  return ICON_BUTTON_CONTROL_SET.has(control);
}

/**
 * Groups consecutive icon-button controls so they render as one horizontal toolbar row; every
 * non-toolbar control becomes its own single-item group.
 */
export function groupToolbarButtons(properties: IndexedSectorProperty[]): IndexedSectorProperty[][] {
  const groups: IndexedSectorProperty[][] = [];
  let currentToolbarGroup: IndexedSectorProperty[] | null = null;

  for (const indexed of properties) {
    const isToolbar = isIconButtonControl(indexed.property.control);
    if (isToolbar) {
      if (!currentToolbarGroup) {
        currentToolbarGroup = [];
        groups.push(currentToolbarGroup);
      }
      currentToolbarGroup.push(indexed);
    } else {
      currentToolbarGroup = null;
      groups.push([indexed]);
    }
  }

  return groups;
}
